import { Navigate, Route, Routes } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SCREENS, isPlanned, me } from './api.js';
import { Layout } from './Layout.js';
import { Loading } from './ui/index.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ProductsPage } from './pages/ProductsPage.js';
import { SageExportPage } from './pages/SageExportPage.js';
import { SupplierFilesPage } from './pages/SupplierFilesPage.js';
import { WorkbenchPage } from './pages/WorkbenchPage.js';
import { WorkbenchCorrectionsPage } from './pages/WorkbenchCorrectionsPage.js';
import { WorkbenchApprovedPage } from './pages/WorkbenchApprovedPage.js';
import { WorkbenchRejectedPage } from './pages/WorkbenchRejectedPage.js';
import { WorkbenchPackPage } from './pages/WorkbenchPackPage.js';
import { ConflictsPage } from './pages/ConflictsPage.js';
import { MatchingQualityPage } from './pages/MatchingQualityPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SettingsPortalsPage } from './pages/SettingsPortalsPage.js';
import { SettingsEmailPage } from './pages/SettingsEmailPage.js';
import { SettingsUsersPage } from './pages/SettingsUsersPage.js';
import { SettingsPermissionsPage } from './pages/SettingsPermissionsPage.js';
import { SettingsCrawlsPage } from './pages/SettingsCrawlsPage.js';
import { HiddenProductsPage } from './pages/HiddenProductsPage.js';
import { InventoryPage } from './pages/InventoryPage.js';
import { OutOfStockPage } from './pages/OutOfStockPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { PrixTvaPage } from './pages/PrixTvaPage.js';
import { B2BPriceVatMatchingPage } from './pages/B2BPriceVatMatchingPage.js';
import { B2BNewProductsPage } from './pages/B2BNewProductsPage.js';
import { SalesStatsPage } from './pages/SalesStatsPage.js';
import { SalesByBrandPage } from './pages/SalesByBrandPage.js';
import { GtinComparePage } from './pages/GtinComparePage.js';
import { EnrichmentPage } from './pages/EnrichmentPage.js';
import { ImageStudioPage } from './pages/ImageStudioPage.js';
import { CrawledNewProductsPage } from './pages/CrawledNewProductsPage.js';
import { FinalEanMatchPage } from './pages/FinalEanMatchPage.js';
import { BarcodeCheckerPage } from './pages/BarcodeCheckerPage.js';
import { ShiparonPage } from './pages/ShiparonPage.js';
import { CarrierReportPage } from './pages/CarrierReportPage.js';
import { WooEnginePage } from './pages/WooEnginePage.js';
import { ShopManagerPage } from './pages/ShopManagerPage.js';
import { ShopOrderPrintPage } from './pages/ShopOrderPrintPage.js';
import { LogisticsOverviewPage } from './pages/LogisticsOverviewPage.js';
import { LogisticsAnalyticsPage } from './pages/LogisticsAnalyticsPage.js';
import { LogisticsRunsheetsPage } from './pages/LogisticsRunsheetsPage.js';
import { RunsheetDetailPage } from './pages/RunsheetDetailPage.js';
import { PaymentsReconciliationPage } from './pages/PaymentsReconciliationPage.js';
import { LogisticsConfigPage } from './pages/LogisticsConfigPage.js';
import { ExchangesPage } from './pages/ExchangesPage.js';
import { ReturnsManagementPage } from './pages/ReturnsManagementPage.js';
import { CancellationsInvestigationPage } from './pages/CancellationsInvestigationPage.js';
import { PublicTrackingPage } from './pages/PublicTrackingPage.js';
import { DeliveredByYmPage } from './pages/DeliveredByYmPage.js';
import { CarrierCaPage } from './pages/CarrierCaPage.js';
import { CompetitorPricesPage } from './pages/CompetitorPricesPage.js';
import { PriceAdjustmentPage } from './pages/PriceAdjustmentPage.js';
import { MargeControlPage } from './pages/MargeControlPage.js';
import { MarketPriceChangesPage } from './pages/MarketPriceChangesPage.js';
import { SeoAuditPage } from './pages/SeoAuditPage.js';
import { MetaCommerceAuditPage } from './pages/MetaCommerceAuditPage.js';
import { ShippingCaPage } from './pages/ShippingCaPage.js';
import { PaymentSlipsPage } from './pages/PaymentSlipsPage.js';
import { StubPage } from './pages/StubPage.js';

/** Boot screen — the session check reads as the app waking up. */
function Boot(): React.ReactElement {
  return (
    <div className="paper-grain flex min-h-screen flex-col items-center justify-center gap-5 bg-paper">
      <span
        aria-hidden="true"
        className="grid h-14 w-14 place-items-center rounded-card bg-herb-700 text-card shadow-card"
      >
        <span className="font-display text-3xl leading-none">M</span>
      </span>
      <div className="text-center">
        <p className="font-display text-2xl text-ink">MaPara</p>
        <p className="eyebrow mt-1 text-ink-faint">GTIN · Prix · Sage 100c</p>
      </div>
      <Loading label="Initialisation de la session…" />
    </div>
  );
}

/** Route table: login is public; every screen sits behind the session check. */
export function AppRoutes(): React.ReactElement {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me, retry: false });

  if (meQuery.isPending) {
    return <Boot />;
  }

  if (meQuery.isError) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/suivi" element={<PublicTrackingPage />} />
        <Route path="/suivi/:code" element={<PublicTrackingPage />} />
        <Route path="/tracking" element={<PublicTrackingPage />} />
        <Route path="/tracking/:code" element={<PublicTrackingPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // brand distributor portal account (brandScope, e.g. "ISDIN, ACM, DERMACEUTIC"):
  // the ONLY page is their competitor prices console — backend enforces the same restriction
  if (meQuery.data.brandScope !== null && meQuery.data.brandScope !== '') {
    return (
      <Routes>
        <Route element={<Layout />}>
          <Route path="/prices/competitors" element={<CompetitorPricesPage />} />
          <Route path="*" element={<Navigate to="/prices/competitors" replace />} />
        </Route>
      </Routes>
    );
  }

  // supply_chain accounts: B2B Grossistes module is the only page
  if (meQuery.data.role === 'supply_chain') {
    return (
      <Routes>
        <Route element={<Layout />}>
          <Route path="/gtin-pricing-vat" element={<B2BPriceVatMatchingPage />} />
        <Route path="/b2b-new-products" element={<B2BNewProductsPage />} />
        <Route path="/sales-stats" element={<SalesStatsPage />} />
        <Route path="/sales-stats/brands" element={<SalesByBrandPage />} />
        <Route path="/sales-stats/by-brand" element={<Navigate to="/sales-stats/brands" replace />} />
        <Route path="/sales-stats" element={<SalesStatsPage />} />
        <Route path="/sales-stats/brands" element={<SalesByBrandPage />} />
        <Route path="/sales-stats/by-brand" element={<Navigate to="/sales-stats/brands" replace />} />
        <Route path="/gtin-compare" element={<GtinComparePage />} />
        <Route path="/crawled-new-products" element={<CrawledNewProductsPage />} />
        <Route path="/final-ean-match" element={<FinalEanMatchPage />} />
        <Route path="/barcode-checker" element={<BarcodeCheckerPage />} />
        {/* S21 — internal catalogue revision workbench (no prices, not for Sage) */}
        <Route path="/enrichment" element={<EnrichmentPage />} />
        {/* S33 — brand packshot & image studio */}
        <Route path="/image-studio" element={<ImageStudioPage />} />
        {/* S30 — quick stock count (barcode gun) */}
        <Route path="/inventory" element={<InventoryPage />} />
        {/* S31 — out of stock: depot availability + competitors still selling. */}
        <Route path="/out-of-stock" element={<OutOfStockPage />} />
          <Route path="*" element={<Navigate to="/gtin-pricing-vat" replace />} />
        </Route>
      </Routes>
    );
  }

  // shop_manager accounts: their module is the only page
  if (meQuery.data.role === 'shop_manager') {
    return (
      <Routes>
        <Route element={<Layout />}>
          <Route path="/shop-manager" element={<ShopManagerPage />} />
        <Route path="/woo-sync" element={<WooEnginePage />} />
        <Route path="/shop-manager/:wooId/:mode" element={<ShopOrderPrintPage />} />
          <Route path="*" element={<Navigate to="/shop-manager" replace />} />
        </Route>
      </Routes>
    );
  }

  // their read-only report — the backend enforces the same restriction
  if (meQuery.data.carrierScope !== null) {
    return (
      <Routes>
        <Route path="/suivi" element={<PublicTrackingPage />} />
        <Route path="/suivi/:code" element={<PublicTrackingPage />} />
        <Route path="/tracking" element={<PublicTrackingPage />} />
        <Route path="/tracking/:code" element={<PublicTrackingPage />} />
        <Route element={<Layout />}>
          <Route path="/carrier-report" element={<CarrierReportPage />} />
          <Route path="/shipping" element={<CarrierReportPage />} />
          <Route path="*" element={<Navigate to="/carrier-report" replace />} />
        </Route>
      </Routes>
    );
  }

  // reviewer & viewer accounts (GTIN & catalog validation operators): strictly scoped to barcode matching & catalog review
  if (meQuery.data.role === 'reviewer' || meQuery.data.role === 'viewer') {
    return (
      <Routes>
        <Route path="/suivi" element={<PublicTrackingPage />} />
        <Route path="/suivi/:code" element={<PublicTrackingPage />} />
        <Route path="/tracking" element={<PublicTrackingPage />} />
        <Route path="/tracking/:code" element={<PublicTrackingPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/workbench" replace />} />
          <Route path="/workbench" element={<WorkbenchPage />} />
          <Route path="/workbench/images" element={<Navigate to="/image-studio" replace />} />
          <Route path="/workbench/corrections" element={<WorkbenchCorrectionsPage />} />
          <Route path="/workbench/approved" element={<WorkbenchApprovedPage />} />
          <Route path="/workbench/rejected" element={<WorkbenchRejectedPage />} />
          <Route path="/workbench/pack" element={<WorkbenchPackPage />} />
          <Route path="/approved" element={<Navigate to="/workbench/approved" replace />} />
          <Route path="/rejected" element={<Navigate to="/workbench/rejected" replace />} />
          <Route path="/pack" element={<Navigate to="/workbench/pack" replace />} />
          <Route path="/workbench/:section" element={<WorkbenchPage />} />
          <Route path="/image-studio" element={<ImageStudioPage />} />
          <Route path="/barcode-checker" element={<BarcodeCheckerPage />} />
          <Route path="/final-ean-match" element={<FinalEanMatchPage />} />
          <Route path="/conflicts" element={<ConflictsPage />} />
          <Route path="/matching-quality" element={<MatchingQualityPage />} />
          <Route path="/crawled-new-products" element={<CrawledNewProductsPage />} />
          <Route path="/gtin-compare" element={<GtinComparePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/enrichment" element={<EnrichmentPage />} />
          <Route path="/hidden-products" element={<HiddenProductsPage />} />
          <Route path="/pricing-vat" element={<PrixTvaPage />} />
          <Route path="/prices/competitors" element={<CompetitorPricesPage />} />
          <Route path="/price-adjustment" element={<PriceAdjustmentPage />} />
          <Route path="/catalogue/marge-control" element={<MargeControlPage />} />
          <Route path="/marge-control" element={<Navigate to="/catalogue/marge-control" replace />} />
          <Route path="/catalogue/market-price-changes" element={<MarketPriceChangesPage />} />
        <Route path="/market-price-changes" element={<Navigate to="/catalogue/market-price-changes" replace />} />
        <Route path="/catalogue/seo" element={<SeoAuditPage />} />
          <Route path="/seo" element={<Navigate to="/catalogue/seo" replace />} />
          <Route path="/catalogue/meta-commerce" element={<MetaCommerceAuditPage />} />
          <Route path="/meta-commerce" element={<Navigate to="/catalogue/meta-commerce" replace />} />
          <Route path="/gtin-pricing-vat" element={<B2BPriceVatMatchingPage />} />
          <Route path="/b2b-new-products" element={<B2BNewProductsPage />} />
          <Route path="*" element={<Navigate to="/workbench" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Standalone public tracking routes accessible even when logged in */}
      <Route path="/suivi" element={<PublicTrackingPage />} />
      <Route path="/suivi/:code" element={<PublicTrackingPage />} />
      <Route path="/tracking" element={<PublicTrackingPage />} />
      <Route path="/tracking/:code" element={<PublicTrackingPage />} />

      <Route element={<Layout />}>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/" element={<DashboardPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/sage-export" element={<SageExportPage />} />
        <Route path="/supplier-files" element={<SupplierFilesPage />} />
        <Route path="/workbench" element={<WorkbenchPage />} />
        <Route path="/workbench/images" element={<Navigate to="/image-studio" replace />} />
        <Route path="/workbench/corrections" element={<WorkbenchCorrectionsPage />} />
        <Route path="/workbench/approved" element={<WorkbenchApprovedPage />} />
        <Route path="/workbench/rejected" element={<WorkbenchRejectedPage />} />
        <Route path="/workbench/pack" element={<WorkbenchPackPage />} />
        <Route path="/approved" element={<Navigate to="/workbench/approved" replace />} />
        <Route path="/rejected" element={<Navigate to="/workbench/rejected" replace />} />
        <Route path="/pack" element={<Navigate to="/workbench/pack" replace />} />
        <Route path="/workbench/:section" element={<WorkbenchPage />} />
        {/* S33 — brand packshot & image studio */}
        <Route path="/image-studio" element={<ImageStudioPage />} />
        {/* Hidden products have their own screen (S29). */}
        <Route path="/hidden-products" element={<HiddenProductsPage />} />
        {/* S30 — quick stock count with a barcode gun. */}
        <Route path="/inventory" element={<InventoryPage />} />
        {/* S31 — out of stock: depot availability + competitors still selling. */}
        <Route path="/out-of-stock" element={<OutOfStockPage />} />
        <Route path="/barcode-checker" element={<BarcodeCheckerPage />} />
        <Route path="/conflicts" element={<ConflictsPage />} />
        <Route path="/matching-quality" element={<MatchingQualityPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/general" element={<SettingsPage />} />
        <Route path="/settings/portals" element={<SettingsPortalsPage />} />
        <Route path="/settings/users" element={<SettingsUsersPage />} />
        <Route path="/settings/permissions" element={<SettingsPermissionsPage />} />
        <Route path="/settings/logistics" element={<LogisticsConfigPage />} />
        <Route path="/settings/email" element={<SettingsEmailPage />} />
        <Route path="/settings/crawls" element={<SettingsCrawlsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/pricing-vat" element={<PrixTvaPage />} />
        <Route path="/prices/competitors" element={<CompetitorPricesPage />} />
        <Route path="/price-adjustment" element={<PriceAdjustmentPage />} />
        <Route path="/catalogue/marge-control" element={<MargeControlPage />} />
        <Route path="/marge-control" element={<Navigate to="/catalogue/marge-control" replace />} />
        <Route path="/catalogue/seo" element={<SeoAuditPage />} />
        <Route path="/seo" element={<Navigate to="/catalogue/seo" replace />} />
        <Route path="/catalogue/meta-commerce" element={<MetaCommerceAuditPage />} />
        <Route path="/meta-commerce" element={<Navigate to="/catalogue/meta-commerce" replace />} />
        <Route path="/gtin-pricing-vat" element={<B2BPriceVatMatchingPage />} />
        <Route path="/b2b-new-products" element={<B2BNewProductsPage />} />
        <Route path="/sales-stats" element={<SalesStatsPage />} />
        <Route path="/sales-stats/brands" element={<SalesByBrandPage />} />
        <Route path="/sales-stats/by-brand" element={<Navigate to="/sales-stats/brands" replace />} />
        <Route path="/gtin-compare" element={<GtinComparePage />} />
        <Route path="/crawled-new-products" element={<CrawledNewProductsPage />} />
        <Route path="/final-ean-match" element={<FinalEanMatchPage />} />
        <Route path="/enrichment" element={<EnrichmentPage />} />
        {/* Logistics module routes */}
        <Route path="/shop-manager" element={<ShopManagerPage />} />
        <Route path="/shop-manager/:wooId/:mode" element={<ShopOrderPrintPage />} />
        <Route path="/shipping" element={<LogisticsOverviewPage />} />
        <Route path="/shipping/overview" element={<LogisticsOverviewPage />} />
        <Route path="/shipping/ca" element={<ShippingCaPage />} />
        <Route path="/shipping/payment-slips" element={<PaymentSlipsPage />} />
        <Route path="/shipping/bordereaux" element={<Navigate to="/shipping/payment-slips" replace />} />
        <Route path="/shipping/reconciliation" element={<ShiparonPage />} />
        <Route path="/shipping/payments" element={<PaymentsReconciliationPage />} />
        <Route path="/shipping/retours" element={<ReturnsManagementPage />} />
        <Route path="/shipping/returns-scan" element={<Navigate to="/shipping/retours" replace />} />
        <Route path="/shipping/echanges" element={<ExchangesPage />} />
        <Route path="/shipping/annulations" element={<CancellationsInvestigationPage />} />
        <Route path="/shipping/analytics" element={<LogisticsAnalyticsPage />} />
        <Route path="/shipping/runsheets" element={<LogisticsRunsheetsPage />} />
        <Route path="/shipping/runsheets/:idRunsheet" element={<RunsheetDetailPage />} />
        <Route path="/shipping/config" element={<LogisticsConfigPage />} />
        {/* S10 — Woo Engine config (EAN meta key, credentials pointers) */}
        <Route path="/woo-sync" element={<WooEnginePage />} />
        <Route path="/shiparon" element={<Navigate to="/shipping/reconciliation" replace />} />
        {/* Accounting module routes */}
        <Route path="/accounting" element={<Navigate to="/accounting/delivered-by-ym" replace />} />
        <Route path="/accounting/delivered-by-ym" element={<DeliveredByYmPage />} />
        <Route path="/accounting/carrier-ca" element={<CarrierCaPage />} />
        {SCREENS.filter((screen) => isPlanned(screen)).map((screen) => (
          <Route
            key={screen.href}
            path={screen.href.slice(1)}
            element={<StubPage screen={screen} />}
          />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
